/**
 * Notes Sync Component
 * 
 * Starts periodic notes synchronization when the app is ready.
 * This ensures notes are always up-to-date across multiple devices.
 */

import React, { useEffect, useContext, useRef } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../state/app/index';
import { startNotesSync, stopNotesSync, syncNow } from '../services/notesSyncService';
import logger from '../logger';

const NotesSync = () => {
  const globalServices = useContext(GlobalStateContext);
  
  // Context should always be available since GlobalStateProvider wraps Routes
  if (!globalServices?.appState) {
    return null;
  }
  
  const [appState, sendAppState] = useActor(globalServices.appState);
  const syncStartedRef = useRef(false);

  useEffect(() => {
    if (!globalServices.appState) {
      return;
    }

    const dispatchSyncedNotes = (notes) => {
      if (notes && Object.keys(notes).length > 0) {
        sendAppState({ type: 'SYNC_NOTES', notes });
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncNow({ onSyncComplete: dispatchSyncedNotes });
      }
    };

    // Track pending retry timeout so we can cancel it on unmount
    let checkTimeoutId = null;

    // Wait for app to be fully primed before starting notes sync
    const checkAndStart = () => {
      const currentState = globalServices.appState.getSnapshot();
      const isPrimed = currentState.context.applicationPrimed;

      if (isPrimed && !syncStartedRef.current) {
        logger.info('Starting notes sync service - app is primed');

        startNotesSync({
          interval: 5 * 60 * 1000, // 5 minutes
          onSyncComplete: (notes) => {
            logger.info({ notesCount: Object.keys(notes).length }, 'Notes sync completed, sending SYNC_NOTES to state machine');
            dispatchSyncedNotes(notes);
          },
        });

        syncNow({ onSyncComplete: dispatchSyncedNotes });
        document.addEventListener('visibilitychange', onVisibilityChange);
        syncStartedRef.current = true;
      } else if (!isPrimed) {
        // Check again in 500ms if not primed yet
        checkTimeoutId = setTimeout(checkAndStart, 500);
      }
    };

    // Subscribe to state changes to detect when app becomes primed
    const subscription = globalServices.appState.subscribe((state) => {
      const isPrimed = state.context?.applicationPrimed;

      if (isPrimed && !syncStartedRef.current) {
        checkAndStart();
      }
    });

    // Start checking immediately
    checkAndStart();

    return () => {
      if (checkTimeoutId) clearTimeout(checkTimeoutId);
      subscription.unsubscribe();
      if (syncStartedRef.current) {
        stopNotesSync();
        syncStartedRef.current = false;
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [globalServices.appState]);

  // This component doesn't render anything
  return null;
};

export default NotesSync;

